import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Loader2, CheckCircle2, Save, Search, GraduationCap, CalendarDays } from "lucide-react";
import {
  useExaminerGetCourseCandidatesQuery,
  useAdminSubmitCourseGradeMutation,
} from "../../api/examsApi";

export default function ExaminerMarkCoursePage() {
  const { dietId = "", courseId = "" } = useParams<{ dietId: string; courseId: string }>();
  const { data, isLoading, refetch, isFetching } = useExaminerGetCourseCandidatesQuery(
    { dietId, courseId },
    { skip: !dietId || !courseId }
  );
  const [submit, { isLoading: saving }] = useAdminSubmitCourseGradeMutation();

  const candidates: any[] = Array.isArray(data?.data) ? data.data : [];

  // Interview diets (PCI / MRI) have no courses: the route carries the literal
  // "panel" and the mark is stored against the interview itself (null course).
  const isPanel = courseId === "panel" || !!data?.is_interview;

  const [scores, setScores] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const next: Record<string, string> = {};
    candidates.forEach((c) => { next[c.application_id] = c.my_score != null ? String(c.my_score) : ""; });
    setScores(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const markedCount = candidates.filter((c) => c.my_score != null).length;
  const total = data?.total ?? candidates.length;
  const pct = total > 0 ? Math.round((markedCount / total) * 100) : 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) =>
        String(c.exam_no ?? "").toLowerCase().includes(q) ||
        String(c.candidate ?? "").toLowerCase().includes(q)
    );
  }, [candidates, search]);

  const save = async (appId: string) => {
    const raw = scores[appId];
    const score = parseFloat(raw);
    if (isNaN(score) || score < 0 || score > 100) {
      setMsg({ ok: false, text: "Enter a mark between 0 and 100." });
      return;
    }
    setBusyId(appId);
    setMsg(null);
    try {
      await submit({
        appId,
        body: { exam_course_id: isPanel ? null : Number(courseId), score },
      }).unwrap();
      setMsg({ ok: true, text: "Mark saved." });
      refetch();
      setTimeout(() => setMsg(null), 2500);
    } catch (e: any) {
      setMsg({ ok: false, text: e?.data?.message || "Could not save mark." });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/exams/examiner/marking" className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 mb-4">
          <ChevronLeft size={16} /> My Marking
        </Link>

        {/* ── Header card ─────────────────────────────────────────────── */}
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-indigo-900 text-white p-6 sm:p-7 shadow-xl shadow-slate-200">
          {/* exam + diet chips */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {data?.exam_title && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/10 border border-white/10 rounded-lg px-2.5 py-1">
                <GraduationCap size={13} /> {data.exam_code ? `${data.exam_code} · ` : ""}{data.exam_title}
              </span>
            )}
            {data?.diet_title && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/10 border border-white/10 rounded-lg px-2.5 py-1">
                <CalendarDays size={13} /> {data.diet_title}
                {data?.diet_status ? <span className="text-white/50">· {data.diet_status}</span> : null}
              </span>
            )}
          </div>

          <h1 className="text-2xl sm:text-3xl font-[1000] tracking-tight leading-tight">
            {data?.course_code ? <span className="text-indigo-300">{data.course_code}</span> : null}
            {data?.course_code ? " — " : ""}{data?.course_name || (isPanel ? "Interview Panel" : "Mark Course")}
          </h1>
          <p className="text-white/60 text-sm mt-1.5">
            {isPanel
              ? "Score each candidate's interview out of 100. You only see your own marks."
              : "Enter each candidate's mark out of 100. You only see your own marks."}
          </p>

          {/* progress */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-white/60 mb-1.5">
              <span>Marked {markedCount} / {total}</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-400 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        {/* toast */}
        {msg && (
          <div className={`mt-4 px-4 py-3 rounded-xl text-sm font-semibold border ${msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
            {msg.text}
          </div>
        )}

        {/* search */}
        {candidates.length > 0 && (
          <div className="mt-5 relative max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search exam no. or name…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-slate-900"
            />
          </div>
        )}

        {/* list */}
        {isLoading ? (
          <div className="py-20 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : candidates.length === 0 ? (
          <div className="mt-5 bg-white border rounded-2xl p-10 text-center text-slate-500">
            {isPanel ? "No candidates in this interview diet." : "No candidates registered for this course."}
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {filtered.map((c, i) => {
              const marked = c.my_score != null;
              const dirty = (scores[c.application_id] ?? "") !== (c.my_score != null ? String(c.my_score) : "");
              return (
                <div
                  key={c.application_id}
                  className={`flex items-center gap-3 bg-white border rounded-2xl p-3 sm:p-3.5 transition-colors ${marked ? "border-emerald-100" : "border-slate-100"}`}
                >
                  <div className={`w-8 h-8 shrink-0 rounded-full grid place-items-center text-[11px] font-black ${marked ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                    {marked ? <CheckCircle2 size={15} /> : i + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="font-black text-slate-900 text-sm truncate">
                      {c.exam_no || c.candidate || c.application_id}
                    </div>
                    {c.exam_no && c.candidate && (
                      <div className="text-[11px] text-slate-400 truncate uppercase tracking-wide">{c.candidate}</div>
                    )}
                  </div>

                  <input
                    type="number" min={0} max={100}
                    value={scores[c.application_id] ?? ""}
                    onChange={(e) => setScores((p) => ({ ...p, [c.application_id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") save(c.application_id); }}
                    placeholder="—"
                    className={`w-20 border rounded-xl px-2 py-2 text-sm text-center font-bold outline-none focus:border-slate-900 ${marked ? "border-emerald-200 text-emerald-700" : "border-slate-200"}`}
                  />
                  <button
                    onClick={() => save(c.application_id)}
                    disabled={busyId === c.application_id || saving || (!dirty && marked)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-black transition-all disabled:opacity-40 ${marked && !dirty ? "bg-slate-100 text-slate-400" : "bg-slate-900 text-white hover:bg-indigo-600"}`}
                  >
                    {busyId === c.application_id ? <Loader2 size={13} className="animate-spin" /> : marked ? <CheckCircle2 size={13} /> : <Save size={13} />}
                    {marked ? (dirty ? "Update" : "Saved") : "Save"}
                  </button>
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="bg-white border rounded-2xl p-8 text-center text-sm text-slate-400">No candidates match "{search}".</div>
            )}
          </div>
        )}

        {isFetching && !isLoading && (
          <div className="mt-3 text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Refreshing…</div>
        )}
      </div>
    </div>
  );
}
